<template>
  <v-container
    fluid
    class="mt-2"
    :class="
      $vuetify.breakpoint.mdAndUp
        ? 'py-5 px-10'
        : '' && $vuetify.breakpoint.smAndUp
        ? 'py-2 px-2'
        : ''
    "
  >
    <PageHeader title="Bookmark Management Guide" />

    <!-- Overview Section -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="text-h5">
        <v-icon left color="primary">mdi-information</v-icon>
        How Bookmark Management Works
      </v-card-title>
      <v-card-text>
        <p class="text-body-1 mb-4">
          LinkBook.io allows you to easily move your bookmarks between your
          browser and our service. This guide will walk you through the complete
          process step by step.
        </p>
        <v-alert type="info" class="mb-4">
          <strong>Pro Tip:</strong> You can export bookmarks from your browser →
          import to LinkBook.io → export from LinkBook.io → import back to your
          browser. This creates a complete backup and sync system!
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Process Flow -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="text-h5">
            <v-icon left color="success">mdi-arrow-right-bold</v-icon>
            Complete Process Flow
          </v-card-title>
          <v-card-text>
            <v-stepper v-model="currentStep" vertical>
              <v-stepper-step :complete="currentStep > 1" step="1">
                Export from Browser
                <small>Get your bookmarks from your browser</small>
              </v-stepper-step>

              <v-stepper-content step="1">
                <v-card class="mb-4" elevation="1">
                  <v-card-text>
                    <h4 class="mb-3">Step 1: Export from Your Browser</h4>
                    <p class="mb-4">
                      First, you need to export your bookmarks from your browser
                      as an HTML file.
                    </p>

                    <v-tabs
                      v-model="browserTab"
                      background-color="transparent"
                      color="primary"
                    >
                      <v-tab>Chrome</v-tab>
                      <v-tab>Firefox</v-tab>
                      <v-tab>Edge</v-tab>
                      <v-tab>Safari</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="browserTab" class="mt-4">
                      <v-tab-item>
                        <v-card flat>
                          <v-card-text>
                            <h5>Google Chrome</h5>
                            <ol class="mt-3">
                              <li>
                                Open Chrome and go to
                                <strong>Bookmarks</strong> →
                                <strong>Bookmark Manager</strong>
                              </li>
                              <li>
                                Click the three dots menu (⋮) in the top right
                              </li>
                              <li>Select <strong>Export bookmarks</strong></li>
                              <li>Choose where to save the HTML file</li>
                            </ol>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>

                      <v-tab-item>
                        <v-card flat>
                          <v-card-text>
                            <h5>Mozilla Firefox</h5>
                            <ol class="mt-3">
                              <li>
                                Open Firefox and go to
                                <strong>Bookmarks</strong> →
                                <strong>Manage Bookmarks</strong>
                              </li>
                              <li>
                                Click <strong>Import and Backup</strong> →
                                <strong>Export Bookmarks to HTML</strong>
                              </li>
                              <li>Choose where to save the HTML file</li>
                            </ol>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>

                      <v-tab-item>
                        <v-card flat>
                          <v-card-text>
                            <h5>Microsoft Edge</h5>
                            <ol class="mt-3">
                              <li>
                                Open Edge and go to <strong>Settings</strong> →
                                <strong>Import browser data</strong>
                              </li>
                              <li>
                                Select <strong>Favorites or bookmarks</strong>
                              </li>
                              <li>Choose <strong>Export to file</strong></li>
                              <li>Choose where to save the HTML file</li>
                            </ol>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>

                      <v-tab-item>
                        <v-card flat>
                          <v-card-text>
                            <h5>Safari</h5>
                            <ol class="mt-3">
                              <li>
                                Open Safari and go to <strong>File</strong> →
                                <strong>Export Bookmarks</strong>
                              </li>
                              <li>Choose where to save the HTML file</li>
                            </ol>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card-text>
                </v-card>
                <v-btn color="primary" @click="currentStep = 2"
                  >Continue to Step 2</v-btn
                >
              </v-stepper-content>

              <v-stepper-step :complete="currentStep > 2" step="2">
                Import to LinkBook.io
                <small>Upload your bookmarks to LinkBook.io</small>
              </v-stepper-step>

              <v-stepper-content step="2">
                <v-card class="mb-4" elevation="1">
                  <v-card-text>
                    <h4 class="mb-3">Step 2: Import to LinkBook.io</h4>
                    <p class="mb-4">
                      Now upload your exported bookmarks to LinkBook.io for
                      better organisation.
                    </p>

                    <v-alert type="info" class="mb-4">
                      <strong>What happens:</strong> LinkBook.io will
                      automatically organize your bookmarks by domain and make
                      them searchable.
                    </v-alert>

                    <ol class="mt-3">
                      <li>
                        Go to the
                        <a href="/import-bookmarks" class="primary--text"
                          >Import Bookmarks page</a
                        >
                      </li>
                      <li>
                        Click "Choose File" and select your exported HTML file
                      </li>
                      <li>Choose whether to overwrite existing bookmarks</li>
                      <li>
                        Click "Upload" and wait for the import to complete
                      </li>
                    </ol>
                  </v-card-text>
                </v-card>
                <v-btn text @click="currentStep = 1">Back to Step 1</v-btn>
                <v-btn color="primary" @click="currentStep = 3"
                  >Continue to Step 3</v-btn
                >
              </v-stepper-content>

              <v-stepper-step :complete="currentStep > 3" step="3">
                Export from LinkBook.io
                <small>Download your organized bookmarks</small>
              </v-stepper-step>

              <v-stepper-content step="3">
                <v-card class="mb-4" elevation="1">
                  <v-card-text>
                    <h4 class="mb-3">Step 3: Export from LinkBook.io</h4>
                    <p class="mb-4">
                      Export your organized bookmarks back to your browser or
                      other services.
                    </p>

                    <v-alert type="success" class="mb-4">
                      <strong>Benefits:</strong> Your bookmarks are now
                      organized by domain and ready for backup or sharing.
                    </v-alert>

                    <ol class="mt-3">
                      <li>
                        Go to the
                        <a href="/export-bookmarks" class="primary--text"
                          >Export Bookmarks page</a
                        >
                      </li>
                      <li>Click "Export Bookmarks" button</li>
                      <li>
                        Your file will be automatically downloaded as
                        "linkbook-export.html"
                      </li>
                    </ol>
                  </v-card-text>
                </v-card>
                <v-btn text @click="currentStep = 2">Back to Step 2</v-btn>
                <v-btn color="primary" @click="currentStep = 4"
                  >Continue to Step 4</v-btn
                >
              </v-stepper-content>

              <v-stepper-step step="4">
                Import back to Browser
                <small>Bring your bookmarks back to your browser</small>
              </v-stepper-step>

              <v-stepper-content step="4">
                <v-card class="mb-4" elevation="1">
                  <v-card-text>
                    <h4 class="mb-3">Step 4: Import back to Your Browser</h4>
                    <p class="mb-4">
                      Import your organized bookmarks back into your browser.
                    </p>

                    <v-tabs
                      v-model="importBrowserTab"
                      background-color="transparent"
                      color="primary"
                    >
                      <v-tab>Chrome</v-tab>
                      <v-tab>Firefox</v-tab>
                      <v-tab>Edge</v-tab>
                      <v-tab>Safari</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="importBrowserTab" class="mt-4">
                      <v-tab-item>
                        <v-card flat>
                          <v-card-text>
                            <h5>Google Chrome</h5>
                            <ol class="mt-3">
                              <li>
                                Open Chrome and go to
                                <strong>Bookmarks</strong> →
                                <strong>Bookmark Manager</strong>
                              </li>
                              <li>
                                Click the three dots menu (⋮) in the top right
                              </li>
                              <li>Select <strong>Import bookmarks</strong></li>
                              <li>
                                Choose the exported HTML file from LinkBook.io
                              </li>
                              <li>
                                Your bookmarks will be imported into an
                                "Imported" folder
                              </li>
                            </ol>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>

                      <v-tab-item>
                        <v-card flat>
                          <v-card-text>
                            <h5>Mozilla Firefox</h5>
                            <ol class="mt-3">
                              <li>
                                Open Firefox and go to
                                <strong>Bookmarks</strong> →
                                <strong>Manage Bookmarks</strong>
                              </li>
                              <li>
                                Click <strong>Import and Backup</strong> →
                                <strong>Import Bookmarks from HTML</strong>
                              </li>
                              <li>
                                Select the exported HTML file from LinkBook.io
                              </li>
                              <li>Choose where to import the bookmarks</li>
                            </ol>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>

                      <v-tab-item>
                        <v-card flat>
                          <v-card-text>
                            <h5>Microsoft Edge</h5>
                            <ol class="mt-3">
                              <li>
                                Open Edge and go to <strong>Settings</strong> →
                                <strong>Import browser data</strong>
                              </li>
                              <li>
                                Select <strong>Favorites or bookmarks</strong>
                              </li>
                              <li>Choose <strong>Import from file</strong></li>
                              <li>
                                Select the exported HTML file from LinkBook.io
                              </li>
                            </ol>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>

                      <v-tab-item>
                        <v-card flat>
                          <v-card-text>
                            <h5>Safari</h5>
                            <ol class="mt-3">
                              <li>
                                Open Safari and go to <strong>File</strong> →
                                <strong>Import Bookmarks</strong>
                              </li>
                              <li>
                                Select the exported HTML file from LinkBook.io
                              </li>
                              <li>Choose where to import the bookmarks</li>
                            </ol>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-tabs-items>

                    <v-alert type="info" class="mt-4">
                      <strong>Note:</strong> Most browsers will create an
                      "Imported" or "Imported Bookmarks" folder to organize
                      newly imported bookmarks. This is normal behavior.
                    </v-alert>
                  </v-card-text>
                </v-card>
                <v-btn text @click="currentStep = 3">Back to Step 3</v-btn>
                <v-btn color="success" @click="currentStep = 1"
                  >Start Over</v-btn
                >
              </v-stepper-content>
            </v-stepper>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Actions -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card elevation="2">
          <v-card-title class="text-h6">
            <v-icon left color="primary">mdi-rocket-launch</v-icon>
            Quick Actions
          </v-card-title>
          <v-card-text>
            <v-btn color="primary" block class="mb-3" href="/import-bookmarks">
              <v-icon left>mdi-upload</v-icon>
              Import Bookmarks
            </v-btn>
            <v-btn color="secondary" block href="/export-bookmarks">
              <v-icon left>mdi-download</v-icon>
              Export Bookmarks
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card elevation="2">
          <v-card-title class="text-h6">
            <v-icon left color="info">mdi-lightbulb</v-icon>
            Tips & Tricks
          </v-card-title>
          <v-card-text>
            <ul class="mb-0">
              <li>Use this process to create backups of your bookmarks</li>
              <li>Organize bookmarks by domain automatically</li>
              <li>Share bookmarks between different browsers</li>
              <li>Keep your bookmarks synced across devices</li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'

export default {
  name: 'BookmarkGuide',
  components: {
    PageHeader
  },
  metaInfo: {
    title: 'Bookmark Management Guide',
    titleTemplate: '%s | LinkBook.io',
    meta: [
      {
        name: 'description',
        content:
          'Complete step-by-step guide for exporting and importing bookmarks between LinkBook.io and your browser. Easy to follow process.'
      }
    ]
  },
  data() {
    return {
      currentStep: 1,
      browserTab: 0,
      importBrowserTab: 0
    }
  }
}
</script>
